/**
 * Route-action contracts for the case detail panel — slice 5d, the two writes
 * a CC operator makes when manually placing a signed transfer:
 *
 *   - **route** — mint the FIRST placement for a signed transfer (`Lead.Status =
 *     "Pending"`). The operator picks an eligible Handling Firm; CC stamps a
 *     fresh `<Lead ID>-NN` Placement at `Decision = "Assigned"` and flips the
 *     lead to `Under Review` — through the portal's `adminRouteLead` door,
 *     whose `createPlacement` also fires the n8n `Lead Sent` webhook (#1302).
 *   - **reroute** — mint the NEXT placement when a prior one came back
 *     Declined or Pulled. The operator picks a (possibly different) firm; CC
 *     creates the new placement, leaves the prior intact (history preserved),
 *     and flips the lead back to `Under Review` — through the portal's
 *     `adminRerouteLead` door, the same `createPlacement` (#1302).
 *
 * Distinct from the placement-action writes (accept / decline / pull /
 * close / settlement): those PATCH an existing placement. Route + reroute
 * CREATE a new one. Command Center does not write that record itself: since
 * #1302 it hands the create to the portal's doors, so the one function that
 * mints Placements (`createPlacement`) is also the one that fires Lead Sent.
 *
 * **Why these are signed-transfer-only.** A Lead becomes a Transfer when
 * `Signed === true` (the FSA returned with the referral). The n8n auto-router
 * handles unsigned leads automatically; for transfers the operator picks the
 * firm. The two routes therefore gate `Signed === true` against the live lead
 * before any write — a stale panel can't manually route a non-transfer.
 *
 * **Eligibility check + override.** The portal's `eligibleHandlingFirms`
 * (worker.js:4568) filters firms by case-type, state, Routing Status === Active,
 * Designation ∈ {Handling, Both}, excluding the originating firm and any with
 * an active in-flight placement on this lead. Annotated with `previouslyDeclined`
 * so the picker can warn the operator. CC ports the same logic at
 * `apps/worker/src/routing/eligibility.ts` (Stage 0 round-robin — ADR-0061
 * defers the picker consolidation onto the Coverage brain).
 *
 * The picker passes `eligibility_override: true` to bypass the eligibility
 * gate — LegalFlare staff's judgment lever (e.g., a routine outside-state escalation
 * or a hand-cleared edge case the matcher misses). Mirrors portal.
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Gates — read against the live lead / placement at the route layer
// ---------------------------------------------------------------------------

/**
 * Lead.Status values from which a manual `route` is valid. The portal route
 * gate is exactly `Status === 'Pending'` (worker.js:6537–6542) — Pending is
 * the state a signed transfer lands in when it first arrives and is waiting
 * for an operator to pick a firm. CC mirrors. (Pending is distinct from
 * `Unreviewed` — that is the unsigned-lead arrival state, which n8n auto-routes.)
 */
export const ROUTE_GATE = ["Pending"] as const;
export function canRoute(status: string | null): boolean {
  return status !== null && (ROUTE_GATE as readonly string[]).includes(status);
}

/**
 * Placement Decision values from which a manual `reroute` is valid. Mirrors
 * portal worker.js:6641–6646 — a reroute applies only when the prior placement
 * came back Declined or Pulled (terminal-for-this-firm; n8n's auto-reroute
 * couldn't find another match, or the operator wants to override the matcher's
 * pick).
 */
export const REROUTE_GATE = ["Declined", "Pulled"] as const;
export function canReroute(decision: string | null): boolean {
  return (
    decision !== null && (REROUTE_GATE as readonly string[]).includes(decision)
  );
}

// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------

/** Body for POST /api/leads/:id/route — pick a firm for a signed transfer. */
export const RouteLeadRequestSchema = z.object({
  /** Airtable record id of the picked Handling Firm. */
  firmId: z.string().min(1),
  /**
   * When true, the eligibility gate is bypassed. Used for hand-cleared edge
   * cases — LegalFlare staff's judgment lever. Defaults to false; the route still
   * verifies the firm exists.
   */
  eligibilityOverride: z.boolean().optional(),
});
export type RouteLeadRequest = z.infer<typeof RouteLeadRequestSchema>;

/** Body for POST /api/placements/:id/reroute — pick the next firm after a decline/pull. */
export const ReroutePlacementRequestSchema = z.object({
  firmId: z.string().min(1),
  eligibilityOverride: z.boolean().optional(),
});
export type ReroutePlacementRequest = z.infer<
  typeof ReroutePlacementRequestSchema
>;

// ---------------------------------------------------------------------------
// Eligible-firm response shape (GET endpoints feed the picker UI)
// ---------------------------------------------------------------------------

/**
 * One candidate in the firm-picker list. Mirrors the portal's
 * `eligibleHandlingFirms` return shape (worker.js:4629–4636) so the picker
 * UI consumes identical JSON whether it's pointed at CC or the portal — keeps
 * the future picker-consolidation follow-up (ADR-0061 deferred) trivial.
 *
 * `previouslyDeclined` is non-null when this firm has a Declined placement
 * on this lead — the picker shows a warning chip with the decline date and
 * reason so the operator doesn't unwittingly re-pick a firm that already said
 * no. (Re-offering Declined firms is the manual-picker semantics; the auto-
 * router excludes them entirely. See ADR-0061.)
 */
export const EligibleFirmSchema = z.object({
  /** Airtable record id of the firm. */
  id: z.string(),
  /** Firm Name (primary text on Firms). */
  name: z.string(),
  /** `Handling` | `Originating` | `Both` | null. Always `Handling` or `Both`
   *  for candidates (the filter rejects others). Carried on the wire for
   *  forward-compat with the ADR-0061 picker consolidation (byte-identical to
   *  portal `eligibleHandlingFirms`); NOT rendered by CC's FirmPickerForm. */
  designation: z.string().nullable(),
  /** State codes the firm operates in (may include `National`). Forward-compat
   *  for the ADR-0061 picker consolidation; not rendered by FirmPickerForm. */
  states: z.array(z.string()),
  /** Case-type labels the firm handles. Forward-compat for the ADR-0061 picker
   *  consolidation; not rendered by FirmPickerForm. */
  caseTypes: z.array(z.string()),
  /** Null when the firm hasn't declined this lead. When present, the picker
   *  shows the date + reason as a "previously declined" warning chip. */
  previouslyDeclined: z
    .object({
      /** ISO date or datetime — whatever the placement carried. */
      date: z.string().nullable(),
      /** The firm's decline rationale. May be empty if the operator declined
       *  without a reason; the picker hides the chip body but keeps the warning. */
      reason: z.string(),
    })
    .nullable(),
});
export type EligibleFirm = z.infer<typeof EligibleFirmSchema>;

/** Response shape for GET /api/leads/:id/eligible-firms (and the reroute
 *  variant on a placement). Mirrors portal worker.js:6756. */
export const EligibleFirmsResponseSchema = z.object({
  count: z.number().int().nonnegative(),
  firms: z.array(EligibleFirmSchema),
});
export type EligibleFirmsResponse = z.infer<typeof EligibleFirmsResponseSchema>;
